import {
  type SkyroverProjectFile,
  readProjectFile,
  sanitizeProjectFilename,
} from '@/features/project/projectFile';

const PROJECT_FILE_TYPES = [
  {
    description: 'Skyrover Project',
    accept: { 'application/json': ['.skyrover'] },
  },
] as const;

export function supportsNativeProjectFiles(): boolean {
  return 'showSaveFilePicker' in window && 'showOpenFilePicker' in window;
}

export function projectNameFromFileName(fileName: string): string {
  const base = fileName.replace(/\.skyrover$/i, '').trim();
  return base || 'Untitled Project';
}

export async function promptSaveProjectLocation(
  suggestedName: string,
): Promise<FileSystemFileHandle | null> {
  try {
    return await window.showSaveFilePicker({
      suggestedName: sanitizeProjectFilename(suggestedName),
      types: [...PROJECT_FILE_TYPES],
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }
    throw error;
  }
}

export async function promptOpenProjectLocation(): Promise<FileSystemFileHandle | null> {
  try {
    const [handle] = await window.showOpenFilePicker({
      types: [...PROJECT_FILE_TYPES],
      multiple: false,
    });
    return handle ?? null;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }
    throw error;
  }
}

export async function writeProjectToHandle(
  handle: FileSystemFileHandle,
  project: SkyroverProjectFile,
): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(JSON.stringify(project, null, 2));
  await writable.close();
}

export async function readProjectFromHandle(
  handle: FileSystemFileHandle,
): Promise<SkyroverProjectFile> {
  const file = await handle.getFile();
  return readProjectFile(file);
}
